import { useState, useEffect, useRef, useCallback } from 'react';

import type { MatchUnitServiceOptions } from '@services/MatchUnitService';
import matchUnitServiceInstance from '@services/MatchUnitService';
import type { LiveMatch } from '@shared-types/match.types';

const useMatch = (originalMatch: LiveMatch, includePlayerEvents = false) => {
  const context = useRef(null);
  const savedOptions = useRef<MatchUnitServiceOptions>({ match: originalMatch, includePlayerEvents });
  const inited = useRef(false);
  const [match, setMatch] = useState<LiveMatch>((originalMatch ?? {}) as LiveMatch);

  const updateMatch = (newMatch: LiveMatch) => {
    if (!newMatch) return;
    setMatch((current) => (newMatch.hash && current.hash === newMatch.hash ? current : newMatch));
  };

  const stopPolling = useCallback(() => {
    if (!savedOptions.current.match?.id) return;

    matchUnitServiceInstance.off(savedOptions.current, updateMatch, context);
    inited.current = false;
  }, []);

  const startPolling = useCallback(() => {
    if (!savedOptions.current.match?.id) return;

    updateMatch(savedOptions.current.match);

    if (inited.current || savedOptions.current.match.hash) return;

    matchUnitServiceInstance.on(savedOptions.current, updateMatch, context);
    inited.current = true;
  }, []);

  useEffect(() => {
    if (!originalMatch?.id) return;
    if (
      originalMatch.id !== savedOptions.current.match?.id ||
      includePlayerEvents !== savedOptions.current.includePlayerEvents
    ) {
      stopPolling();
    }

    savedOptions.current = {
      match: originalMatch,
      includePlayerEvents,
    };

    startPolling();
  }, [includePlayerEvents, originalMatch, startPolling, stopPolling]);

  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  return match;
};

export default useMatch;
